import { vec3, vec4 } from "gl-matrix";
import { ViewPort } from "../View/ViewPort";
import { Context } from "../Engine";
import { project } from "@spi/Common/Math";

export class TextElement {
    private _text: string;
    private _position: vec3;
    private lastWindowPosition: vec3;
    private threshold: number;

    public element: HTMLSpanElement;
    public offset: vec3;

    public inWorldSpace: boolean;
    public centerText: boolean;

    private positionLink: PositionLink;

    constructor(inWorldSpace: boolean = true, centerText: boolean = true, cssClass?: string) {

        this.inWorldSpace = inWorldSpace;
        this.centerText = centerText;

        this.element = document.createElement('span');
        this.element.setAttribute('class', cssClass ? cssClass : 'default');

        this.lastWindowPosition = null;
        this.threshold = 0.5;

        this.text = '';

        this.positionLink = null;
        this._position = vec3.fromValues(0, 0, 0);
        this.offset = vec3.fromValues(0, 0, 0);
    }

    public set position(pos: vec3) {
        this._position = pos;
        this.positionLink = null;
    }

    public get position(): vec3 {
        if (this._position) {
            return this._position;
        } else {
            let pos = this.positionLink.position;

            // TODO: add offset
            return vec3.fromValues(pos[0], pos[1], pos[2]);
        }
    }

    public linkPosition(obj: PositionLink) {
        this.positionLink = obj;
        this._position = null;
    }

    public get text() {
        return this._text;
    }

    public set text(str: string) {
        this._text = str;
        this.element.textContent = str;
    }

    public draw(ctx: Context) {

        let pos = vec3.clone(this.position);
        vec3.add(pos, pos, this.offset);

        let textPosition: vec3;
        if (this.inWorldSpace) {

            // Calculate the text position in 3D and convert it back to a 2D position relative to the screen (which is used in ortho mode)
            let projected = project(pos, ctx.projectionMatrix, ctx.modelViewMatrix, ctx.viewPort);

            textPosition = vec3.fromValues(projected[0], projected[1], projected[2]);
        } else {
            textPosition = pos;
        }

        let windowPosition = this.convertPositionToWindowPosition(ctx.viewPort, textPosition);

        // Only update text position when it has actually changed (i.e. moved more than <threshold> pixels).
        // This draw method can be called up to 60 times per second, it would be a significant bottleneck if 
        // the browser has to micro adjust the position of the text every frame.
        if (this.differs(windowPosition)) {
            this.lastWindowPosition = windowPosition;
            if (this.centerText) {
                this.element.style.left = (windowPosition[0] - this.element.offsetWidth / 2) + 'px';
            } else {
                this.element.style.left = windowPosition[0] + 'px';
            }
            this.element.style.top = windowPosition[1] + 'px';
            this.element.style.display = windowPosition[2] > 1.0 ? 'none' : 'inline-block';
        }
    }

    private differs(windowPosition: vec3): boolean {
        if (this.lastWindowPosition) {
            if (Math.abs(this.lastWindowPosition[0] - windowPosition[0]) >= this.threshold) {
                return true;
            }
            if (Math.abs(this.lastWindowPosition[1] - windowPosition[1]) >= this.threshold) {
                return true;
            }

            if (windowPosition[2] > 1.0 && this.element.style.display !== 'none') {
                return true; // if Z > 1 we have to hide the element
            }

            return false;
        } else {
            return true;
        }
    }

    private convertPositionToWindowPosition(viewPort: ViewPort, textPosition: vec3): vec3 {
        return vec3.fromValues(
            textPosition[0] + viewPort.width / 2,
            viewPort.height - (textPosition[1] + viewPort.height / 2),
            textPosition[2]
        );
    }

}

type PositionLink = {
    position: vec3 | vec4
}